import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import Modal from '../../components/common/Modal/Modal';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import { getMyStudentAssignments, getMyStudentProjects } from '../../services/studentService';
import { submitAssignmentWithUrls, getMySubmission } from '../../services/assignmentService';
import AssignmentChecklist from '../../components/common/AssignmentChecklist/AssignmentChecklist';
import { formatDate, formatDateTime, isPastDeadline } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import styles from './StudentAssignments.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const StudentAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissionUrls, setSubmissionUrls] = useState([{ title: '', url: '' }]);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAssignment, setViewingAssignment] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignmentsData, projectsData] = await Promise.all([
        getMyStudentAssignments(),
        getMyStudentProjects()
      ]);
      setAssignments(assignmentsData);
      setProjects(projectsData);
      
      // Fetch submissions for each assignment
      const submissionsData = {};
      for (const assignment of assignmentsData) {
        try {
          const submission = await getMySubmission(assignment.id);
          submissionsData[assignment.id] = submission || null; // Handle null response
        } catch (error) {
          // No submission yet or error
          console.log(`No submission for assignment ${assignment.id}`);
          submissionsData[assignment.id] = null;
        }
      }
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter assignments by selected project
  const filteredAssignments = assignments.filter(assignment => {
    if (selectedProject === 'all') return true;
    return assignment.project_id === selectedProject;
  });

  const getAssignmentStatus = (assignment) => {
    const submission = submissions[assignment.id];
    if (!submission) {
      // Check if overdue
      if (isPastDeadline(assignment.deadline)) {
        return 'overdue';
      }
      return 'assigned';
    }
    return submission.status;
  };

  const handleSubmitClick = (assignment) => {
    setSelectedAssignment(assignment);
    const existingSubmission = submissions[assignment.id];
    
    if (existingSubmission && existingSubmission.submission_urls) {
      setSubmissionUrls(existingSubmission.submission_urls.length > 0 
        ? existingSubmission.submission_urls 
        : [{ title: '', url: '' }]);
      setSubmissionNotes(existingSubmission.submission_notes || '');
    } else {
      setSubmissionUrls([{ title: '', url: '' }]);
      setSubmissionNotes('');
    }
    
    setShowSubmitModal(true);
  };

  const handleAddUrlField = () => {
    setSubmissionUrls([...submissionUrls, { title: '', url: '' }]);
  };

  const handleRemoveUrlField = (index) => {
    if (submissionUrls.length > 1) {
      setSubmissionUrls(submissionUrls.filter((_, i) => i !== index));
    }
  };

  const handleUrlChange = (index, field, value) => {
    const updated = [...submissionUrls];
    updated[index][field] = value;
    setSubmissionUrls(updated);
  };

  const handleSubmit = async () => {
    // Validate at least one URL is provided
    const validUrls = submissionUrls.filter(u => u.title.trim() && u.url.trim());
    if (validUrls.length === 0 && !submissionNotes.trim()) {
      toast.warning('Please provide at least one URL or submission notes');
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        submission_urls: validUrls,
        submission_notes: submissionNotes
      };
      
      await submitAssignmentWithUrls(selectedAssignment.id, submissionData);
      toast.success('Assignment submitted successfully!');
      setShowSubmitModal(false);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewGradedWork = (assignment) => {
    setViewingAssignment(assignment);
    setShowViewModal(true);
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (row) => <span className={styles.assignmentTitle}>{row.title}</span>
    },
    {
      header: 'Description',
      accessor: 'description',
      render: (row) => (
        <span className={styles.description}>
          {row.description?.substring(0, 50)}{row.description?.length > 50 ? '...' : ''}
        </span>
      )
    },
    {
      header: 'Due Date',
      accessor: 'deadline',
      render: (row) => {
        const isOverdue = isPastDeadline(row.deadline) && getAssignmentStatus(row) !== 'submitted' && getAssignmentStatus(row) !== 'completed';
        return (
          <span className={isOverdue ? styles.overdue : styles.dueDate}>
            {formatDate(row.deadline)}
          </span>
        );
      }
    },
    {
      header: 'Max Score',
      accessor: 'max_score',
      render: (row) => (
        <span className={styles.maxScore}>
          {row.max_score > 0 ? row.max_score : 'N/A'}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (row) => {
        const status = getAssignmentStatus(row);
        return <StatusBadge status={status} />;
      }
    },
    {
      header: 'Score',
      accessor: 'score',
      render: (row) => {
        const submission = submissions[row.id];
        if (submission && submission.score !== null && submission.score !== undefined) {
          const isPartialCredit = submission.status === 'returned';
          return (
            <span className={isPartialCredit ? styles.partialScore : styles.score}>
              {submission.score}/{row.max_score}
              {isPartialCredit && <span className={styles.partialLabel}> (Partial)</span>}
            </span>
          );
        }
        return <span className={styles.noScore}>-</span>;
      }
    },
    {
      header: 'Actions',
      accessor: 'actions',
      sortable: false,
      render: (row) => {
        const status = getAssignmentStatus(row);
        
        if (status === 'completed') {
          return (
            <button
              className={styles.viewBtn}
              onClick={() => handleViewGradedWork(row)}
            >
              View
            </button>
          );
        } else if (status === 'submitted' || status === 'submitted_late') {
          return (
            <span className={styles.submittedText}>
              ✓ Submitted
            </span>
          );
        } else if (status === 'returned') {
          return (
            <button
              className={`${styles.submitBtn} ${styles.resubmitBtn}`}
              onClick={() => handleSubmitClick(row)}
            >
              Resubmit
            </button>
          );
        } else {
          return (
            <button
              className={styles.submitBtn}
              onClick={() => handleSubmitClick(row)}
            >
              Submit
            </button>
          );
        }
      }
    }
  ];

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading assignments..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Assignments</h1>
        <div className={styles.projectSelector}>
          <label htmlFor="projectSelect">Project:</label>
          <select
            id="projectSelect"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className={styles.projectSelect}
          >
            <option value="all">All Projects</option>
            {sortProjectsByName(projects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        {filteredAssignments.length === 0 ? (
          <div className={styles.empty}>
            <p>No assignments found</p>
          </div>
        ) : (
          <Table columns={columns} data={filteredAssignments} />
        )}
      </Card>

      {/* Submission Modal */}
      {showSubmitModal && selectedAssignment && (
        <Modal
          isOpen={showSubmitModal}
          onClose={() => setShowSubmitModal(false)}
          title={submissions[selectedAssignment.id] ? "Resubmit Assignment" : "Submit Assignment"}
          size="large"
        >
          <div className={styles.modalContent}>
            <div className={styles.assignmentInfo}>
              <h3>{selectedAssignment.title}</h3>
              <AssignmentChecklist
                description={selectedAssignment.description}
                taskState={submissions[selectedAssignment.id]?.task_state}
                assignmentId={selectedAssignment.id}
                interactive
                onChange={(updated) => setSubmissions((prev) => ({
                  ...prev, [selectedAssignment.id]: { ...prev[selectedAssignment.id], ...updated },
                }))}
              />
              <div className={styles.deadlineInfo}>
                <strong>Deadline:</strong> {formatDate(selectedAssignment.deadline)}
                {submissions[selectedAssignment.id]?.new_deadline && (
                  <span className={styles.extendedDeadline}>
                    (Extended to: {formatDate(submissions[selectedAssignment.id].new_deadline)})
                  </span>
                )}
              </div>
              {submissions[selectedAssignment.id]?.feedback && (
                <div className={styles.feedbackBox}>
                  <strong>Mentor's Feedback:</strong>
                  <p>{submissions[selectedAssignment.id].feedback}</p>
                </div>
              )}
            </div>

            <div className={styles.urlsSection}>
              <div className={styles.sectionHeader}>
                <label>Submission URLs</label>
                <Button size="small" onClick={handleAddUrlField} loading={loading}>+ Add URL</Button>
              </div>
              
              {submissionUrls.map((urlItem, index) => (
                <div key={index} className={styles.urlGroup}>
                  <div className={styles.urlFields}>
                    <div className={styles.formGroup}>
                      <input
                        type="text"
                        value={urlItem.title}
                        onChange={(e) => handleUrlChange(index, 'title', e.target.value)}
                        placeholder="URL Title (e.g., Design Document, Code Repository)"
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <input
                        type="url"
                        value={urlItem.url}
                        onChange={(e) => handleUrlChange(index, 'url', e.target.value)}
                        placeholder="https://..."
                        className={styles.input}
                      />
                    </div>
                  </div>
                  {submissionUrls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveUrlField(index)}
                      className={styles.removeBtn}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="submissionNotes">Your Notes/Comments</label>
              <textarea
                id="submissionNotes"
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
                placeholder="Add any additional notes or comments about your submission..."
                rows={6}
                className={styles.textarea}
              />
            </div>

            <div className={styles.modalActions}>
              <Button onClick={() => setShowSubmitModal(false)} loading={loading}
                variant="outline"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={loading}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : (submissions[selectedAssignment.id] ? 'Resubmit Assignment' : 'Submit Assignment')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* View Graded Work Modal */}
      {showViewModal && viewingAssignment && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="Assignment Grade Details"
        >
          <div className={styles.viewModalContent}>
            <div className={styles.assignmentInfo}>
              <h3>{viewingAssignment.title}</h3>
              <AssignmentChecklist
                description={viewingAssignment.description}
                taskState={submissions[viewingAssignment.id]?.task_state}
                assignmentId={viewingAssignment.id}
              />
            </div>

            {submissions[viewingAssignment.id] && (
              <>
                <div className={styles.gradeSection}>
                  <h4>Your Grade</h4>
                  {viewingAssignment.max_score > 0 ? (
                    <div className={styles.scoreDisplay}>
                      <span className={styles.scoreLabel}>Score:</span>
                      <span className={styles.scoreLarge}>
                        {submissions[viewingAssignment.id].score}/{viewingAssignment.max_score}
                      </span>
                      <span className={styles.scorePercentage}>
                        ({Math.round((submissions[viewingAssignment.id].score / viewingAssignment.max_score) * 100)}%)
                      </span>
                    </div>
                  ) : (
                    <div className={styles.nonScorableMsg}>
                      <span>✓ Non-scorable assignment - Completed</span>
                    </div>
                  )}
                </div>

                {submissions[viewingAssignment.id].feedback && (
                  <div className={styles.feedbackSection}>
                    <h4>Mentor's Feedback</h4>
                    <div className={styles.feedbackBox}>
                      {submissions[viewingAssignment.id].feedback}
                    </div>
                  </div>
                )}

                <div className={styles.submissionDetailsSection}>
                  <h4>Your Submission</h4>
                  {submissions[viewingAssignment.id].submission_urls && 
                   submissions[viewingAssignment.id].submission_urls.length > 0 && (
                    <div className={styles.submittedUrlsList}>
                      {submissions[viewingAssignment.id].submission_urls.map((urlItem, idx) => (
                        <div key={idx} className={styles.submittedUrlItem}>
                          <div className={styles.urlItemTitle}>{urlItem.title}</div>
                          <a href={urlItem.url} target="_blank" rel="noopener noreferrer" className={styles.urlItemLink}>
                            Link: {urlItem.url}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  {submissions[viewingAssignment.id].submission_notes && (
                    <div className={styles.notesDisplay}>
                      <strong>Your Notes:</strong>
                      <p>{submissions[viewingAssignment.id].submission_notes}</p>
                    </div>
                  )}
                </div>

                <div className={styles.submissionMeta}>
                  <span>Submitted: {formatDateTime(submissions[viewingAssignment.id].submission_date)}</span>
                  {submissions[viewingAssignment.id].reviewed_at && (
                    <span>Graded: {formatDateTime(submissions[viewingAssignment.id].reviewed_at)}</span>
                  )}
                </div>
              </>
            )}

            <div className={styles.modalActions}>
              <Button onClick={() => setShowViewModal(false)} loading={loading}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentAssignments;

